import { useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Gamepad2 } from 'lucide-react';

import { env } from '@/env';
import { useGameSelection } from '@/hooks/useGameSelection';
import { useHaptics } from '@/hooks/useHaptics';
import { GamePicker } from '@/components/games/GamePicker';

/**
 * La riga del GIOCO — «Gioco · <gioco attivo>», e un tocco apre il selettore (`GamePicker`).
 *
 * 🔴 Sta in testa a `SettingsList`, quindi in ENTRAMBI i posti che lo montano: la scheda Impostazioni
 * (`/settings`, la ⚙️ della barra) e la tasca Impostazioni dello Zaino. Fino al 2026-10-04 era scritta a
 * mano solo nello Zaino (`Backpack.tsx`), fuori dall'elenco condiviso: quando la ⚙️ e' diventata una
 * scheda (I-318) la scheda e' nata senza. Luca, I-334: «in tma adesso in bottom menu c'è rotella per
 * impostazioni - ma in quella sezione da quel punto manca il selettore del gioco, aggiungilo».
 *
 * ⚠️ Il selettore va in un PORTALE su `document.body`. E' `fixed inset-0` senza portale suo, e tutti e due
 * gli ospiti hanno un antenato con `transform` (lo `Sheet` dello Zaino; l'entrata `screen-in-*` della
 * scheda), che per un `fixed` fa da blocco contenitore: il selettore resterebbe chiuso li' dentro invece di
 * coprire lo schermo. Nello Zaino stava apposta FUORI dallo `Sheet`; il portale e' la stessa cosa, per
 * entrambi i posti. I colori del tema stanno su `:root` (`lib/appearance.ts`), quindi il portale li ha.
 *
 * Con `VITE_FEATURE_CR_ONLY` la riga resta, senza tocco, e dice «Clash Royale»: come prima nello Zaino.
 *
 * `senzaMostraTutti` (facoltativa) toglie «Mostra tutti» dal selettore: la passa `SettingsList` quando la v3 e' accesa, perche' il disegno v3 non ha una Hub «senza gioco»
 * (SCELTE-20261028-004, la (C-2)). Senza, il selettore e' quello di sempre.
 */
export function RigaGioco({ senzaMostraTutti }: { senzaMostraTutti?: boolean }) {
  const { t } = useTranslation('general');
  const haptics = useHaptics();
  const { activeGame } = useGameSelection();
  const [aperto, setAperto] = useState(false);

  return (
    <>
      <button
        type="button"
        data-testid="riga-gioco"
        onClick={
          env.featureCrOnly
            ? undefined
            : () => {
                haptics.impact('light');
                setAperto(true);
              }
        }
        className="card-edge flex items-center gap-3 rounded-xl bg-bg-secondary p-3 text-left transition-opacity active:opacity-80"
      >
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-bg-tertiary text-brand">
          <Gamepad2 className="h-4 w-4" />
        </span>
        <span className="flex min-w-0 flex-1 flex-col">
          <span className="truncate text-sm font-semibold text-fg">
            {t('game', { defaultValue: 'Game' })}
          </span>
          <span className="truncate text-xs text-fg-muted">
            {env.featureCrOnly
              ? 'Clash Royale'
              : (activeGame?.name ?? t('game_pick', { defaultValue: 'Choose a game' }))}
          </span>
        </span>
      </button>
      {createPortal(<GamePicker open={aperto} onClose={() => setAperto(false)} senzaMostraTutti={senzaMostraTutti} />, document.body)}
    </>
  );
}
